import { v2IsUtcZone, v2Now, v2ViewerZone, v2ZoneAbbreviation, v2ZonedParts, v2ZonedText } from './time-zone';

/**
 * THE v2 day and time in words. Every page that shows a calendar day or a time uses these, so
 * it reads the same everywhere and follows the airline's CompanySetting.date_format.
 *
 * - Month first ("Aug 16", "Aug 16, 2026") for MM-DD-YYYY; day first ("16 Aug",
 *   "16 Aug 2026") for DD-MM-YYYY, YYYY-MM-DD and no setting (words have no year-first form).
 * - A date-only value ('YYYY-MM-DD') is that calendar day as written, never shifted by a
 *   time zone (contract dates, "bids closed" days).
 * - A timestamp is an instant, read in the VIEWER's zone (Anton 2026-10-10, "local time
 *   everywhere"): the profile's time zone, else the browser's (`time-zone.ts`).
 * - '' for a missing or unreadable value.
 */

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A calendar day; `month` is 0-based. */
export interface V2Day {
    year: number;
    month: number;
    day: number;
}

export type V2DateFormat = string | null | undefined;

/** 'always' adds the year; 'auto' only when it is not `today`'s year (viewer's zone); 'never' leaves it out. */
export type V2YearMode = 'always' | 'auto' | 'never';

const NBSP = ' ';
const DATE_ONLY =/^(\d{4})-(\d{2})-(\d{2})$/;
/** 'YYYY-MM-DDTHH:MM[:SS[.fff]]' (or a space for the T) with no Z and no offset. */
const NO_ZONE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** The calendar day of a value: 'YYYY-MM-DD' as written; a timestamp or Date in `zone` (the viewer's). */
export function v2DayOf(value: string | Date | V2Day | null | undefined, zone: string = v2ViewerZone()): V2Day | null {
    if (!value) {
        return null;
    }
    if (typeof value === 'object' && !(value instanceof Date)) {
        return value;
    }
    if (typeof value === 'string') {
        const match = DATE_ONLY.exec(value.trim());
        if (match) {
            return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
        }
    }
    const date = value instanceof Date ? value : v2InstantOf(value);
    if (!date || isNaN(date.getTime())) {
        return null;
    }
    const parts = v2ZonedParts(date, zone);
    return { year: parts.year, month: parts.month, day: parts.day };
}

/** "16 Aug", "16 Aug 2026", or month first for MM-DD-YYYY ("Aug 16", "Aug 16, 2026"). */
export function v2DayText(value: string | Date | V2Day | null | undefined, dateFormat: V2DateFormat,
                          year: V2YearMode = 'always', today: Date = new Date(v2Now())): string {
    const day = v2DayOf(value);
    if (!day || !MONTHS[day.month]) {
        return '';
    }
    const month = MONTHS[day.month];
    const withYear = year === 'always' || (year === 'auto' && day.year !== v2ZonedParts(today).year);
    // No-break spaces: a day never wraps between its parts ("25 / Feb") in a narrow column.
    if (monthFirst(dateFormat)) {
        return withYear ? `${month}${NBSP}${day.day},${NBSP}${day.year}` : `${month}${NBSP}${day.day}`;
    }
    return withYear ? `${day.day}${NBSP}${month}${NBSP}${day.year}` : `${day.day}${NBSP}${month}`;
}

/**
 * A calendar date that the server may send as a timestamp at 00:00 UTC (contract start and end,
 * price dates, an expiry day): its UTC day, so it never moves a day across zones.
 */
export function v2CalendarDayText(value: string | Date | V2Day | null | undefined, dateFormat: V2DateFormat,
                                  year: V2YearMode = 'always'): string {
    return v2DayText(v2DayOf(value, 'UTC'), dateFormat, year);
}

/** A day and a clock time: "21 Sep 2026, 09:14" (or "Sep 21, 2026, 09:14"). */
export function v2DayTimeText(day: V2Day | null, hours: number, minutes: number, dateFormat: V2DateFormat): string {
    const text = v2DayText(day, dateFormat, 'always');
    return text ? `${text}, ${pad(hours)}:${pad(minutes)}` : '';
}

/**
 * Any time stamp (created, updated, sent, signed in…) in the viewer's zone, labelled, in the
 * airline's day order: "21 Sep 2026, 09:14 CEST" ('auto' drops this year: "21 Sep, 09:14 CEST").
 * '' for a missing or unreadable value. `seconds` adds ":05" for audit-style detail lines.
 */
export function v2StampText(value: V2InstantLike, dateFormat: V2DateFormat = null,
                            year: V2YearMode = 'always', seconds = false, today: Date = new Date(v2Now())): string {
    const at = v2InstantOf(value);
    if (!at) {
        return '';
    }
    const zone = v2ViewerZone();
    const parts = v2ZonedParts(at, zone);
    const day = v2DayText({ year: parts.year, month: parts.month, day: parts.day }, dateFormat, year, today);
    const clock = `${pad(parts.hours)}:${pad(parts.minutes)}${seconds ? `:${pad(parts.seconds)}` : ''}`;
    return `${day}, ${clock}${NBSP}${v2ZoneAbbreviation(at, zone)}`;
}

/** "09:14" in the viewer's zone, unlabelled: only for a just-now save status ("Saved 09:14"). */
export function v2BareClockText(value: V2InstantLike): string {
    const at = v2InstantOf(value);
    if (!at) {
        return '';
    }
    const parts = v2ZonedParts(at);
    return `${pad(parts.hours)}:${pad(parts.minutes)}`;
}

/** The clock time alone in the viewer's zone, labelled: "09:14 CEST". */
export function v2ClockText(value: V2InstantLike): string {
    const at = v2InstantOf(value);
    if (!at) {
        return '';
    }
    const zone = v2ViewerZone();
    const parts = v2ZonedParts(at, zone);
    return `${pad(parts.hours)}:${pad(parts.minutes)}${NBSP}${v2ZoneAbbreviation(at, zone)}`;
}

/** "Oct 2026": a month and year; a calendar date as written, a timestamp's in the viewer's zone. */
export function v2MonthText(value: string | Date | V2Day | null | undefined): string {
    const day = v2DayOf(value);
    return day && MONTHS[day.month] ? `${MONTHS[day.month]}${NBSP}${day.year}` : '';
}

/** A range of days, the year said once when both ends share it: "1 Jan – 31 Dec 2027". */
export function v2DayRangeText(start: string | V2Day | null | undefined, end: string | V2Day | null | undefined,
                               dateFormat: V2DateFormat): string {
    const a = v2DayOf(start);
    const b = v2DayOf(end);
    if (!a || !b) {
        return '';
    }
    const left = v2DayText(a, dateFormat, a.year === b.year ? 'never' : 'always');
    return `${left} – ${v2DayText(b, dateFormat, 'always')}`;
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** What a deadline can arrive as: an ISO string, a Date, a moment or a Luxon DateTime. */
export type V2InstantLike = string | Date | { toDate(): Date } | { toJSDate(): Date } | null | undefined;

/**
 * An instant from any of the shapes the pages hold; null for a missing or unreadable value.
 * A date-only 'YYYY-MM-DD' is a deadline's day, so it is the END of that day (23:59:59 UTC),
 * as the server reads a bare date (fix/round-deadline-time).
 */
export function v2InstantOf(value: V2InstantLike): Date | null {
    if (!value) {
        return null;
    }
    let date: Date;
    if (value instanceof Date) {
        date = value;
    } else if (typeof value === 'string') {
        const text = value.trim();
        const match = DATE_ONLY.exec(text);
        date = match
            ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59))
            // A date-time with no zone is UTC, as the server stores it (bid history JSON): never the browser's zone.
            : new Date(NO_ZONE.test(text) ? `${text.replace(' ', 'T')}Z` : text);
    } else if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
        date = (value as { toDate(): Date }).toDate();
    } else if (typeof (value as { toJSDate?: () => Date }).toJSDate === 'function') {
        date = (value as { toJSDate(): Date }).toJSDate();
    } else {
        return null;
    }
    return isNaN(date.getTime()) ? null : date;
}

/**
 * THE round deadline in words (Anton 2026-10-10, "local time everywhere"): in the viewer's
 * zone, labelled with its abbreviation, always day first whatever the airline's day order, so
 * a deadline reads the same on every page.
 *
 *   'long'  (default): "Sun 11 Oct 2026, 23:59 CEST"
 *   'short' (tables):  "11 Oct 2026, 23:59 CEST"
 *
 * '' for a missing or unreadable value. Pair it with `v2UtcTip` for the hover and screen readers.
 */
export function v2DeadlineText(value: V2InstantLike, form: 'long' | 'short' = 'long'): string {
    const at = v2InstantOf(value);
    return at ? v2ZonedText(at, form, v2ViewerZone()) : '';
}

/**
 * The same instant in UTC, for a time's hover (`title`) and screen readers: "21:59 UTC" when the
 * UTC day is the viewer's day, else "12 Oct 2026, 03:59 UTC". '' when the viewer is in UTC
 * (the text already says it) or for no value.
 */
export function v2UtcTip(value: V2InstantLike): string {
    const at = v2InstantOf(value);
    const zone = v2ViewerZone();
    if (!at || v2IsUtcZone(zone)) {
        return '';
    }
    const local = v2ZonedParts(at, zone);
    const utc = v2ZonedParts(at, 'UTC');
    const clock = `${pad(utc.hours)}:${pad(utc.minutes)}${NBSP}UTC`;
    return local.year === utc.year && local.month === utc.month && local.day === utc.day
        ? clock
        : `${utc.day}${NBSP}${MONTHS[utc.month]}${NBSP}${utc.year}, ${clock}`;
}

/** The signed-in airline's day order (CompanySetting.date_format), as classic's jft-datetime reads it. */
export function v2DateFormatOf(user: { company?: { settings?: { date_format?: string | null } | null } | null } | null)
    : V2DateFormat {
    return user?.company?.settings?.date_format ?? null;
}

/** The pattern a typed date follows, in the airline's order: day first unless it says otherwise. */
export type V2DayInputPattern = 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD';

export function v2DayInputPattern(dateFormat: V2DateFormat): V2DayInputPattern {
    return dateFormat === 'MM-DD-YYYY' || dateFormat === 'YYYY-MM-DD' ? dateFormat : 'DD-MM-YYYY';
}

/** A calendar date 'YYYY-MM-DD' as the date input shows it: "05-08-2026" (or "08-05-2026"); '' for none. */
export function v2DayInputText(value: string | null | undefined, dateFormat: V2DateFormat): string {
    const match = DATE_ONLY.exec((value ?? '').trim());
    if (!match) {
        return '';
    }
    const [, year, month, day] = match;
    switch (v2DayInputPattern(dateFormat)) {
        case 'MM-DD-YYYY':
            return `${month}-${day}-${year}`;
        case 'YYYY-MM-DD':
            return `${year}-${month}-${day}`;
        default:
            return `${day}-${month}-${year}`;
    }
}

const TYPED_ISO = /^(\d{4})[-/. ]+(\d{1,2})[-/. ]+(\d{1,2})$/;
const TYPED_DAY = /^(\d{1,2})[-/. ]+(\d{1,2})[-/. ]+(\d{4})$/;

/**
 * Typed text as 'YYYY-MM-DD', read ONLY in the airline's order (never the other order when that
 * one fails: "03-04-2026" is one day, not two); year-first ISO is always read. A four-digit year,
 * and a day that exists. null for anything else.
 */
export function v2ParseDayInput(text: string | null | undefined, dateFormat: V2DateFormat): string | null {
    const value = (text ?? '').trim();
    let year: number;
    let month: number;
    let day: number;
    const iso = TYPED_ISO.exec(value);
    const typed = iso ? null : TYPED_DAY.exec(value);
    if (iso) {
        [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
    } else if (typed && v2DayInputPattern(dateFormat) === 'MM-DD-YYYY') {
        [month, day, year] = [Number(typed[1]), Number(typed[2]), Number(typed[3])];
    } else if (typed && v2DayInputPattern(dateFormat) === 'DD-MM-YYYY') {
        [day, month, year] = [Number(typed[1]), Number(typed[2]), Number(typed[3])];
    } else {
        return null;
    }
    if (month < 1 || month > 12 || day < 1) {
        return null;
    }
    // An impossible day ("31-02-2026") is unreadable, not quietly the next month.
    if (new Date(Date.UTC(year, month - 1, day)).getUTCDate() !== day) {
        return null;
    }
    return `${year}-${pad(month)}-${pad(day)}`;
}

function monthFirst(dateFormat: V2DateFormat): boolean {
    return dateFormat === 'MM-DD-YYYY';
}

function pad(value: number): string {
    return String(value).padStart(2, '0');
}
